'use client';

import { UseRowsReturn, useBreakpoints } from '@nettbureau/featured-core';
import { Button, HStack, Text, VStack, useCss } from '@nettbureau/vibrant-ui';
import { useTranslate } from '@tolgee/react';
import { Fragment, ReactNode, useContext, useEffect, useState } from 'react';
import { getShouldRenderRow } from '../../functions';
import { ContactLink } from '../ContactLink';
import { ContainerContent } from '../container/ContainerContent';
import { ContainerTitle } from '../container/ContainerTitle';
import {
  ContainerWithTheme,
  FeaturedContainer,
} from '../container/FeaturedContainer';
import { ConfigContext } from '../provider/config-provider';
import { getTheme, getTokenFromVibrant } from '../theme';

export interface FeaturedProps<T> {
  title: string;
  contactUrl: string | null;
  showButton?: boolean;
  cardRenderer: (item: T, index: number) => ReactNode;
  rows: UseRowsReturn<T>;
}

export function Featured<T>(props: FeaturedProps<T>) {
  const { cardRenderer, rows, title, showButton, contactUrl } = props;
  const { setVisible } = rows;
  const [showButtonState, setShowButtonState] = useState(showButton ?? true);
  const { t } = useTranslate();
  const { isMobile } = useBreakpoints();

  const { cardVisibleRows, globalThemeVariant, theme } =
    useContext(ConfigContext);

  const buttonStyles = useCss((input) => {
    return {
      background: getTokenFromVibrant(
        globalThemeVariant === 'default'
          ? getTheme(theme).default.button.backgroundColor
          : getTheme(theme).secondary.button.backgroundColor,
        input.theme,
      ),
    };
  });

  const buttonTextStyles = useCss((input) => {
    return {
      color: getTokenFromVibrant(
        globalThemeVariant === 'default'
          ? getTheme(theme).default.button.textColor
          : getTheme(theme).secondary.button.textColor,
        input.theme,
      ),
    };
  });

  useEffect(() => {
    if (showButton) {
      setShowButtonState(showButton);
    }
  }, [showButton]);

  return getShouldRenderRow(rows) ? (
    <ContainerWithTheme>
      <FeaturedContainer>
        <ContainerTitle alignment="center" title={title} />
        <ContainerContent>
          <VStack data-featured-card-container gap={isMobile ? 8 : 10}>
            <HStack
              maxWidth={350}
              justify="center"
              wrap
              align="stretch"
              gap={4}
            >
              {rows.rows.map(({ items, visible }, index) => {
                return visible ? (
                  <Fragment key={index}>
                    {items.map((item, index) => {
                      return cardRenderer(item, index);
                    })}
                  </Fragment>
                ) : null;
              })}
            </HStack>

            {showButtonState && rows.rows.length > Number(cardVisibleRows) && (
              <HStack align="center" justify="center">
                <Button
                  css={buttonStyles}
                  onPress={() => {
                    setVisible();
                    setShowButtonState(false);
                  }}
                >
                  <Text css={buttonTextStyles}>{t('show_more')}</Text>
                </Button>
              </HStack>
            )}

            {contactUrl && <ContactLink url={contactUrl} />}
          </VStack>
        </ContainerContent>
      </FeaturedContainer>
    </ContainerWithTheme>
  ) : null;
}
